import { parseJwt } from './utilities';
import { TIERS, TIER_DEFAULT } from '../constants/tiering';

const TIERS_AV = Object.entries(TIERS).map((i) => i[1]);

const deriveTierFromEntitlements = (entitlements) => {
  try {
    const lmod = entitlements?.LMOD;

    // If LMOD is a string like 'base' | 'premium'
    if (typeof lmod === 'string') {
      const normalized = lmod.toLowerCase();
      return TIERS_AV.includes(normalized) ? normalized : TIER_DEFAULT;
    }

    return TIER_DEFAULT;
  } catch (e) {
    // On any failure, default
    return TIER_DEFAULT;
  }
};

/**
 * Parse JWT token to extract user entitlements and authentication information
 * @param {string} jwtToken - The JWT token string
 * @returns {Object|null} Parsed entitlement information or null if parsing fails
 */
export const parseJWTEntitlements = (jwtToken) => {
  try {
    const decoded = parseJwt(jwtToken);
    if (!decoded) return null;

    // Parse custom:entitlements JSON string
    let entitlements = {};
    try {
      const entitlementsStr = decoded['custom:entitlements'];
      if (entitlementsStr) {
        entitlements = JSON.parse(entitlementsStr);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse entitlements JSON:', e);
    }

    return {
      hasActiveSubscription: !!entitlements.LMOD,
      tier: deriveTierFromEntitlements(entitlements),
      vimeoUserId: decoded['custom:vimeo_id'] || null,
      userId: decoded.sub || null,
      // Additional fields that might be useful
      cognitoGroups: decoded['cognito:groups'] || [],
      customSub: decoded['custom:sub'] || null,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('JWT parsing error:', error);
    return null;
  }
};

/**
 * Check if user has specific entitlement
 * @param {string} jwtToken - The JWT token string
 * @param {string} entitlement - The entitlement to check (e.g., 'LMOD')
 * @returns {boolean} Whether user has the entitlement
 */
export const hasJWTEntitlement = (jwtToken, entitlement = 'LMOD') => {
  try {
    const decoded = parseJwt(jwtToken);
    const entitlementsStr = decoded['custom:entitlements'];
    if (!entitlementsStr) return false;

    const entitlements = JSON.parse(entitlementsStr);
    return !!entitlements[entitlement];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error checking JWT entitlement:', error);
    return false;
  }
};

/**
 * Get user tier from JWT token
 * @param {string} jwtToken - The JWT token string
 * @returns {string} User tier ('premium', 'base', or default)
 */
export const getJWTTier = (jwtToken, rtJwtEntitlement) => {
  try {
    if (rtJwtEntitlement) {
      return parseJWTEntitlements(jwtToken)?.tier || TIER_DEFAULT;
    }
    const decoded = parseJwt(jwtToken);
    return decoded?.tierId?.split('#')[1] || TIER_DEFAULT;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting JWT tier:', error);
    return TIER_DEFAULT;
  }
};
